import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { api } from '@/api/client';
import type {
  MessengerLikeLikerPreview,
  MessengerMessage,
  MessengerMessageReadReceiptRow,
} from '@/api/client';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { ChevronRightIcon, DeleteIcon, FaMessengerIcon, PushPinIcon, VisibilityIcon } from '@/components/ui/Icons';
import { formatMessengerActivityLabel } from '@/components/Messenger/shared/messengerActivityDateLabel';
import { isMessengerOptimisticMessageId } from '@/constants/messengerOptimistic';
import { copyPlainTextToClipboard } from '@/lib/copyPlainTextToClipboard';
import { useToast } from '@/contexts/ToastContext';
import {
  captureDocumentSelection,
  getMessageCopyMenuLabel,
  resolveMessageCopyText,
  scheduleRestoreDocumentSelection,
} from '@/components/Messenger/shared/messages/messageContextCopy';
import { resolveDmMessageFileTarget } from '@/components/Messenger/shared/messages/messengerAttachmentTarget';
import {
  buildMessageAttachmentContextMenuItems,
  messageContextCopyTextIcon,
} from '@/components/Messenger/shared/messages/messageContextAttachmentMenuItems';
import './MessageContextMenu.css';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const SUBMENU_CLOSE_MS = 160;

interface MessageContextMenuProps {
  msg: MessengerMessage | null;
  anchorPosition: { x: number; y: number } | null;
  isOwn: boolean;
  currentUserId: string | null;
  /** `group` — пункт «Просмотрено» для списка прочитавших */
  threadVariant?: 'direct' | 'group';
  /** Тред «Сохранённые»: удаление без лимита 24 ч */
  relaxDeleteAgeLimit?: boolean;
  onClose: () => void;
  onReply: (msg: MessengerMessage) => void;
  onForward: (msg: MessengerMessage) => void;
  onDelete: (msg: MessengerMessage) => void;
  onEdit?: (msg: MessengerMessage) => void;
  onPin?: (msg: MessengerMessage) => void;
  onUnpin?: (msg: MessengerMessage) => void;
  onSelect?: (msg: MessengerMessage) => void;
  onToggleLike?: (msg: MessengerMessage) => void;
  /** Выделение внутри сообщения на момент ПКМ */
  selectedText?: string;
  /** Полный plain text сообщения для копирования без выделения */
  messageText?: string;
}

function likeLikerDisplayName(p: MessengerLikeLikerPreview): string {
  const s = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
  return s || 'Участник';
}

/** Иконка строки; при появлении других reaction_type — расширить ветвление или данные с API */
function reactionRowIcon(): ReactNode {
  return (
    <span className="messageContextMenu__reactionEmoji" aria-hidden>
      ❤️
    </span>
  );
}

/** Дата и время просмотра в подменю группы — общий формат активности мессенджера. */
function formatGroupReadReceiptDateTime(iso: string): string {
  return formatMessengerActivityLabel(iso, { includeTime: true });
}

export function MessageContextMenu({
  msg,
  anchorPosition,
  isOwn,
  onClose,
  onReply,
  onForward,
  onDelete,
  onEdit,
  onPin,
  onUnpin,
  onSelect,
  onToggleLike,
  currentUserId,
  relaxDeleteAgeLimit = false,
  threadVariant = 'direct',
  selectedText = '',
  messageText = '',
}: MessageContextMenuProps) {
  const { showSuccess, showError } = useToast();
  const [reactionsSubmenuAnchor, setReactionsSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [readSubmenuAnchor, setReadSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [likeLikers, setLikeLikers] = useState<MessengerLikeLikerPreview[] | null>(null);
  const [likeLikersLoading, setLikeLikersLoading] = useState(false);
  const [likeLikersError, setLikeLikersError] = useState<string | null>(null);
  const [groupReaders, setGroupReaders] = useState<MessengerMessageReadReceiptRow[] | null>(null);
  const [groupReadersLoading, setGroupReadersLoading] = useState(false);
  const [groupReadersError, setGroupReadersError] = useState<string | null>(null);
  const likersLoadedForIdRef = useRef<string | null>(null);
  const likersInFlightRef = useRef(false);
  const groupReadersLoadedForIdRef = useRef<string | null>(null);
  const groupReadersInFlightRef = useRef(false);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
    }
    submenuCloseTimerRef.current = setTimeout(() => {
      setReactionsSubmenuAnchor(null);
      setReadSubmenuAnchor(null);
      submenuCloseTimerRef.current = null;
    }, SUBMENU_CLOSE_MS);
  }, []);

  const cancelSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!anchorPosition) {
      setReactionsSubmenuAnchor(null);
      setReadSubmenuAnchor(null);
      setLikeLikers(null);
      setLikeLikersLoading(false);
      setLikeLikersError(null);
      likersLoadedForIdRef.current = null;
      likersInFlightRef.current = false;
      setGroupReaders(null);
      setGroupReadersLoading(false);
      setGroupReadersError(null);
      groupReadersLoadedForIdRef.current = null;
      groupReadersInFlightRef.current = false;
    }
  }, [anchorPosition]);

  const ensureLikeLikersLoaded = useCallback(async () => {
    if (!msg || isMessengerOptimisticMessageId(msg.id)) return;
    if (likersInFlightRef.current) return;
    if (likersLoadedForIdRef.current === msg.id) return;

    likersInFlightRef.current = true;
    setLikeLikersLoading(true);
    setLikeLikersError(null);
    try {
      const { likers } = await api.messenger.getMessageLikeLikers(msg.thread_id, msg.id);
      setLikeLikers(likers);
      likersLoadedForIdRef.current = msg.id;
    } catch {
      setLikeLikersError('Не удалось загрузить');
      setLikeLikers([]);
      likersLoadedForIdRef.current = msg.id;
    } finally {
      setLikeLikersLoading(false);
      likersInFlightRef.current = false;
    }
  }, [msg]);

  const ensureGroupReadersLoaded = useCallback(async () => {
    if (!msg || isMessengerOptimisticMessageId(msg.id)) return;
    if (groupReadersInFlightRef.current) return;
    if (groupReadersLoadedForIdRef.current === msg.id) return;

    groupReadersInFlightRef.current = true;
    setGroupReadersLoading(true);
    setGroupReadersError(null);
    try {
      const { readers } = await api.messenger.getMessageReadReceipts(msg.thread_id, msg.id);
      setGroupReaders(readers);
      groupReadersLoadedForIdRef.current = msg.id;
    } catch {
      setGroupReadersError('Не удалось загрузить');
      setGroupReaders([]);
      groupReadersLoadedForIdRef.current = msg.id;
    } finally {
      setGroupReadersLoading(false);
      groupReadersInFlightRef.current = false;
    }
  }, [msg]);

  if (!msg) return null;

  const isDeleted = Boolean(msg.deleted_at);
  const isForwarded = Boolean(
    msg.forwarded_from_message_id || (msg.forwarded_from_label && msg.forwarded_from_label.trim())
  );
  const ageMs = Date.now() - new Date(msg.created_at).getTime();
  const withinDay = ageMs < ONE_DAY_MS;

  const canEdit = isOwn && !isDeleted && !isForwarded && (withinDay || relaxDeleteAgeLimit);
  const canDelete = isOwn && !isDeleted && (withinDay || relaxDeleteAgeLimit);
  const canTogglePin =
    !isDeleted &&
    (msg.message_kind ?? 'user') === 'user' &&
    !isMessengerOptimisticMessageId(msg.id) &&
    Boolean(onPin || onUnpin);

  const likeCount = msg.like_count ?? 0;
  const showReactionsSubmenu =
    !isDeleted &&
    likeCount > 0 &&
    !isMessengerOptimisticMessageId(msg.id);

  const showGroupReadSubmenu =
    threadVariant === 'group' &&
    !isDeleted &&
    (msg.message_kind ?? 'user') === 'user' &&
    Boolean(msg.read_at?.trim()) &&
    !isMessengerOptimisticMessageId(msg.id);

  const itemsForRender: { key: string; node: ReactNode }[] = [];

  const copyPayload = resolveMessageCopyText(selectedText, messageText);
  const showCopyText = !isDeleted && copyPayload.length > 0;
  const copyMenuLabel = getMessageCopyMenuLabel(selectedText.trim().length > 0);
  const fileTarget = resolveDmMessageFileTarget(msg);

  if (!isDeleted) {
    itemsForRender.push({
      key: 'reply',
      node: (
        <li key="reply" className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              onReply(msg);
              onClose();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              <FaMessengerIcon icon="replyd" size={21} />
            </span>
            <span className="context-menu__item-text">Ответить</span>
          </button>
        </li>
      ),
    });
  }

  if (showCopyText) {
    itemsForRender.push({
      key: 'copy',
      node: (
        <li key="copy" className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const savedSelection = captureDocumentSelection();
              void (async () => {
                const ok = await copyPlainTextToClipboard(copyPayload);
                if (ok) showSuccess('Текст скопирован');
                else showError('Не удалось скопировать текст');
                onClose();
                scheduleRestoreDocumentSelection(savedSelection);
              })();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              {messageContextCopyTextIcon()}
            </span>
            <span className="context-menu__item-text">{copyMenuLabel}</span>
          </button>
        </li>
      ),
    });
  }

  itemsForRender.push(
    ...buildMessageAttachmentContextMenuItems(fileTarget, { showSuccess, showError }, onClose),
  );

  if (!isDeleted && currentUserId && onToggleLike) {
    const liked = Boolean(msg.liked_by_me);
    itemsForRender.push({
      key: 'like',
      node: (
        <li key="like" className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              onToggleLike(msg);
              onClose();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              <i
                className={liked ? 'fa-regular fa-heart' : 'fa-solid fa-heart'}
                style={{ fontSize: 18, width: 18, textAlign: 'center' }}
                aria-hidden
              />
            </span>
            <span className="context-menu__item-text">{liked ? 'Не нравится' : 'Нравится'}</span>
          </button>
        </li>
      ),
    });
  }

  if (showReactionsSubmenu) {
    itemsForRender.push({
      key: 'reactions',
      node: (
        <li
          key="reactions"
          className="context-menu__list-item"
          onMouseEnter={(e) => {
            cancelSubmenuClose();
            setReadSubmenuAnchor(null);
            setReactionsSubmenuAnchor(e.currentTarget);
            void ensureLikeLikersLoaded();
          }}
          onMouseLeave={scheduleSubmenuClose}
        >
          <button type="button" className="context-menu__item context-menu__action_with-sub">
            <span className="context-menu__iconCell" aria-hidden>
              <FaMessengerIcon icon="icons" size={18} />
            </span>
            <span className="context-menu__item-text">Реакции</span>
            <ChevronRightIcon size={16} className="context-menu__action-chevron" />
          </button>
          {reactionsSubmenuAnchor ? (
            <ContextMenuSubmenu
              anchorEl={reactionsSubmenuAnchor}
              onMouseEnter={cancelSubmenuClose}
              onMouseLeave={scheduleSubmenuClose}
            >
              <ul className="context-menu__list" role="list">
                {likeLikersLoading ? (
                  <li className="context-menu__list-item" role="presentation">
                    <span className="messageContextMenu__reactionsStatus">Загрузка…</span>
                  </li>
                ) : likeLikersError ? (
                  <li className="context-menu__list-item" role="presentation">
                    <span className="messageContextMenu__reactionsStatus messageContextMenu__reactionsStatus_error">
                      {likeLikersError}
                    </span>
                  </li>
                ) : !likeLikers || likeLikers.length === 0 ? (
                  <li className="context-menu__list-item" role="presentation">
                    <span className="messageContextMenu__reactionsStatus">Нет реакций</span>
                  </li>
                ) : (
                  likeLikers.map((liker) => (
                    <li key={liker.person_id} className="context-menu__list-item" role="presentation">
                      <div className="messageContextMenu__reactionRow">
                        <span className="context-menu__iconCell" aria-hidden>
                          {reactionRowIcon()}
                        </span>
                        <span className="messageContextMenu__reactionName" title={likeLikerDisplayName(liker)}>
                          {likeLikerDisplayName(liker)}
                        </span>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </ContextMenuSubmenu>
          ) : null}
        </li>
      ),
    });
  }

  if (showGroupReadSubmenu) {
    itemsForRender.push({
      key: 'group-read',
      node: (
        <li
          key="group-read"
          className="context-menu__list-item"
          onMouseEnter={(e) => {
            cancelSubmenuClose();
            setReactionsSubmenuAnchor(null);
            setReadSubmenuAnchor(e.currentTarget);
            void ensureGroupReadersLoaded();
          }}
          onMouseLeave={scheduleSubmenuClose}
        >
          <button type="button" className="context-menu__item context-menu__action_with-sub">
            <span className="context-menu__iconCell" aria-hidden>
              <VisibilityIcon size={18} />
            </span>
            <span className="context-menu__item-text">Просмотрено</span>
            <ChevronRightIcon size={16} className="context-menu__action-chevron" />
          </button>
          {readSubmenuAnchor ? (
            <ContextMenuSubmenu
              anchorEl={readSubmenuAnchor}
              onMouseEnter={cancelSubmenuClose}
              onMouseLeave={scheduleSubmenuClose}
            >
              <ul className="context-menu__list" role="list">
                {groupReadersLoading ? (
                  <li className="context-menu__list-item" role="presentation">
                    <span className="messageContextMenu__reactionsStatus">Загрузка…</span>
                  </li>
                ) : groupReadersError ? (
                  <li className="context-menu__list-item" role="presentation">
                    <span className="messageContextMenu__reactionsStatus messageContextMenu__reactionsStatus_error">
                      {groupReadersError}
                    </span>
                  </li>
                ) : !groupReaders || groupReaders.length === 0 ? (
                  <li className="context-menu__list-item" role="presentation">
                    <span className="messageContextMenu__reactionsStatus">Пока никто</span>
                  </li>
                ) : (
                  groupReaders.map((row) => {
                    const dateTimeLine = formatGroupReadReceiptDateTime(row.read_at);
                    const titleFull = format(new Date(row.read_at), 'd MMMM yyyy, HH:mm', { locale: ru });
                    return (
                      <li key={row.person_id} className="context-menu__list-item" role="presentation">
                        <div className="messageContextMenu__readRow" title={titleFull}>
                          <span className="context-menu__iconCell" aria-hidden>
                            <VisibilityIcon size={16} />
                          </span>
                          <span className="messageContextMenu__readRowMain">
                            <span className="messageContextMenu__readRowName">{row.display_name}</span>
                            <span className="messageContextMenu__readRowTime">{dateTimeLine}</span>
                          </span>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </ContextMenuSubmenu>
          ) : null}
        </li>
      ),
    });
  }

  if (!isDeleted) {
    itemsForRender.push({
      key: 'forward',
      node: (
        <li key="forward" className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              onForward(msg);
              onClose();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              <FaMessengerIcon icon="share" size={18} />
            </span>
            <span className="context-menu__item-text">Переслать</span>
          </button>
        </li>
      ),
    });
  }

  if (canTogglePin) {
    const pinned = Boolean(msg.is_pinned);
    itemsForRender.push({
      key: pinned ? 'unpin' : 'pin',
      node: (
        <li key={pinned ? 'unpin' : 'pin'} className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              if (pinned) onUnpin?.(msg);
              else onPin?.(msg);
              onClose();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              <PushPinIcon size={18} />
            </span>
            <span className="context-menu__item-text">{pinned ? 'Открепить' : 'Закрепить'}</span>
          </button>
        </li>
      ),
    });
  }

  if (canEdit && onEdit) {
    itemsForRender.push({
      key: 'edit',
      node: (
        <li key="edit" className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              onEdit(msg);
              onClose();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              <FaMessengerIcon icon="pencil" size={18} />
            </span>
            <span className="context-menu__item-text">Редактировать</span>
          </button>
        </li>
      ),
    });
  }

  if (!isDeleted && onSelect) {
    itemsForRender.push({
      key: 'select',
      node: (
        <li key="select" className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item"
            onClick={() => {
              onSelect(msg);
              onClose();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              <FaMessengerIcon icon="circle-check" size={18} />
            </span>
            <span className="context-menu__item-text">Выбрать</span>
          </button>
        </li>
      ),
    });
  }

  if (canDelete) {
    itemsForRender.push({
      key: 'delete',
      node: (
        <li key="delete" className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__item context-menu__item_danger"
            onClick={() => {
              onDelete(msg);
              onClose();
            }}
            role="menuitem"
          >
            <span className="context-menu__iconCell" aria-hidden>
              <DeleteIcon size={18} />
            </span>
            <span className="context-menu__item-text">Удалить</span>
          </button>
        </li>
      ),
    });
  }

  if (itemsForRender.length === 0) return null;

  return (
    <ContextMenu isOpen={anchorPosition !== null} onClose={onClose} anchorPosition={anchorPosition}>
      <ul className="context-menu__list">{itemsForRender.map((x) => x.node)}</ul>
    </ContextMenu>
  );
}
